import { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, Shield, Terminal, ShieldCheck, Target, Search, Bug, Cloud, Lock, ArrowRight, Phone } from "lucide-react";
import { cn } from "../lib/utils";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import GradientText from "./GradientText";
import ClickToCall from "./ui/ClickToCall";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false);
  };

  return (
    <motion.nav
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full"
      style={{
        background: isScrolled
          ? "rgba(26, 26, 46, 0.4)"
          : "transparent",
        backdropFilter: isScrolled ? "blur(20px) saturate(180%)" : undefined,
        borderBottom: isScrolled ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(255, 255, 255, 0)",
        boxShadow: isScrolled 
          ? "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : undefined,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <div
        className="w-full px-4 sm:px-6 lg:px-8 mx-auto"
        style={{
          maxWidth: "100vw",
        }}
      >
        <div className="flex items-center justify-between h-16">
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center gap-2 group">
              <img
                src="/images/cipher_character_notext.png"
                alt="CIPHER Security Logo"
                className="w-12 h-12 object-contain transition-all duration-300 group-hover:scale-105"
                style={{
                  filter: "brightness(1.1) contrast(1.1)"
                }}
              />
              <div className="hidden sm:block">
                <div
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#ffffff",
                    letterSpacing: "-0.01em",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem"
                  }}
                  className="group-hover:opacity-90"
                >
                  <span>CIPHER</span>
                  <span
                    style={{
                      background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 50%, #00a86b 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      color: "#00a86b",
                      fontWeight: 700,
                    }}
                  >
                    Security
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    marginTop: "-0.2rem",
                    letterSpacing: "0.02em",
                    textTransform: "uppercase" as const,
                    width: "100%",
                    maxWidth: "180px",
                    textAlign: "center" as const,
                    whiteSpace: "nowrap" as const
                  }}
                >
                  <GradientText
                    colors={["#00a86b", "#06b6d4", "#fbbf24", "#06b6d4", "#00a86b"]}
                    animationSpeed={3}
                    showBorder={false}
                    className="navbar-gradient-text"
                  >
                    Prevent&nbsp;•&nbsp;Detect&nbsp;•&nbsp;Respond
                  </GradientText>
                </div>
              </div>
            </a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a 
              href="/"
              style={{
                color: "#d1d5db",
                fontWeight: 500,
                fontSize: "1rem",
                borderRadius: "8px",
                transition: "color 0.2s",
                textDecoration: "none",
                padding: "0.5rem",
              }}
              onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
            >
              Home
            </a>

            <div className="relative group">
              <button
                style={{
                  color: "#d1d5db",
                  fontWeight: 500,
                  fontSize: "1rem",
                  borderRadius: "8px",
                  transition: "color 0.2s",
                  background: "none",
                  border: "none",
                  padding: "0.5rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                Services
                <ChevronDown className="w-4 h-4" />
              </button>
              
              <div 
                className="absolute top-full left-0 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                style={{
                  width: "320px",
                  background: "rgba(26, 26, 46, 0.4)",
                  borderRadius: "16px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(20px) saturate(180%)",
                  padding: "1rem",
                  zIndex: 50,
                }}
              >
                <Link
                  to="/services/application-security"
                  className="flex w-full text-left p-3 group/item"
                  style={{
                    borderRadius: "8px",
                    background: "none",
                    border: "none",
                    transition: "background 0.2s",
                    textDecoration: "none"
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "rgba(0, 168, 107, 0.1)")}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center mr-3"
                    style={{
                      background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                      borderRadius: "6px",
                      opacity: 0.9,
                    }}
                  >
                    <Shield className="w-4 h-4" style={{ color: "#ffffff" }} />
                  </div>
                  <div className="flex items-center">
                    <div style={{ fontWeight: 600, color: "#ffffff", fontSize: "0.95rem" }}>
                      Application Security
                    </div>
                  </div>
                </Link>

                <Link
                  to="/services/security-advisory"
                  className="flex w-full text-left p-3 group/item"
                  style={{
                    borderRadius: "8px",
                    background: "none",
                    border: "none",
                    transition: "background 0.2s",
                    textDecoration: "none"
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "rgba(167, 139, 250, 0.1)")}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center mr-3"
                    style={{
                      background: "linear-gradient(135deg, rgba(167, 139, 250, 0.2), rgba(167, 139, 250, 0.1))",
                      borderRadius: "6px",
                      opacity: 0.9,
                    }}
                  >
                    <ShieldCheck className="w-4 h-4" style={{ color: "#a78bfa" }} />
                  </div>
                  <div className="flex items-center">
                    <div style={{ fontWeight: 600, color: "#ffffff", fontSize: "0.95rem" }}>
                      Security Advisory Services
                    </div>
                  </div>
                </Link>

                <Link
                  to="/services/offensive-security"
                  className="flex w-full text-left p-3 group/item"
                  style={{
                    borderRadius: "8px",
                    background: "none",
                    border: "none",
                    transition: "background 0.2s",
                    textDecoration: "none"
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "rgba(248, 113, 113, 0.1)")}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center mr-3"
                    style={{
                      background: "linear-gradient(135deg, rgba(248, 113, 113, 0.2), rgba(251, 191, 36, 0.1))",
                      borderRadius: "6px",
                      opacity: 0.9,
                    }}
                  >
                    <Target className="w-4 h-4" style={{ color: "#f87171" }} />
                  </div>
                  <div className="flex items-center">
                    <div style={{ fontWeight: 600, color: "#ffffff", fontSize: "0.95rem" }}>
                      Offensive Security
                    </div>
                  </div>
                </Link>

                <Link
                  to="/services/cloud-security"
                  className="flex w-full text-left p-3 group/item"
                  style={{
                    borderRadius: "8px",
                    background: "none",
                    border: "none",
                    transition: "background 0.2s",
                    textDecoration: "none"
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)")}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center mr-3"
                    style={{
                      background: "linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(16, 185, 129, 0.1))",
                      borderRadius: "6px",
                      opacity: 0.9,
                    }}
                  >
                    <Cloud className="w-4 h-4" style={{ color: "#3b82f6" }} />
                  </div>
                  <div className="flex items-center">
                    <div style={{ fontWeight: 600, color: "#ffffff", fontSize: "0.95rem" }}>
                      Cloud Security
                    </div>
                  </div>
                </Link>

                <Link
                  to="/services/devsecops"
                  className="flex w-full text-left p-3 group/item"
                  style={{
                    borderRadius: "8px",
                    background: "none",
                    border: "none",
                    transition: "background 0.2s",
                    textDecoration: "none"
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "rgba(74, 222, 128, 0.1)")}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <div
                    className="w-8 h-8 flex items-center justify-center mr-3"
                    style={{
                      background: "linear-gradient(135deg, rgba(74, 222, 128, 0.2), rgba(0, 168, 107, 0.1))",
                      borderRadius: "6px",
                      opacity: 0.9,
                    }}
                  >
                    <Terminal className="w-4 h-4" style={{ color: "#4ade80" }} />
                  </div>
                  <div className="flex items-center">
                    <div style={{ fontWeight: 600, color: "#ffffff", fontSize: "0.95rem" }}>
                      DevSecOps
                    </div>
                  </div>
                </Link>

                <div className="pt-3 mt-3 border-t" style={{ borderColor: "rgba(6, 182, 212, 0.2)" }}>
                  <Link
                    to="/services"
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-semibold transition-all"
                    style={{
                      background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                      color: "#ffffff",
                      textDecoration: "none",
                      fontSize: "0.9rem"
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow = "0 4px 16px 0 rgba(0, 168, 107, 0.25)";
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    View All Services
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>

            <a 
              href="/about"
              style={{
                color: "#d1d5db",
                fontWeight: 500,
                fontSize: "1rem",
                borderRadius: "8px",
                transition: "color 0.2s",
                textDecoration: "none",
                padding: "0.5rem",
              }}
              onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
            >
              About
            </a>

{/* 
            <div className="relative group">
              <button
                style={{
                  color: "#d1d5db",
                  fontWeight: 500,
                  fontSize: "1rem",
                  borderRadius: "8px",
                  transition: "color 0.2s",
                  background: "none",
                  border: "none",
                  padding: "0.5rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                }}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                Resources
                <ChevronDown className="w-4 h-4" />
              </button>
              
              <div 
                className="absolute top-full left-0 mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200"
                style={{
                  width: "400px",
                  background: "rgba(26, 26, 46, 0.4)",
                  borderRadius: "16px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(20px) saturate(180%)",
                  padding: "1rem",
                  zIndex: 50,
                }}
              >
                <a
                  href="/blog"
                  className="block p-3"
                  style={{
                    borderRadius: "8px",
                    color: "#ffffff",
                    background: "none",
                    transition: "background 0.2s",
                    textDecoration: "none",
                  }}
                  onMouseOver={e => (e.currentTarget.style.background = "rgba(6, 182, 212, 0.1)")}
                  onMouseOut={e => (e.currentTarget.style.background = "none")}
                >
                  <div style={{ fontWeight: 600 }}>Security Blog</div>
                  <p style={{ fontSize: "0.95rem", color: "#d1d5db", margin: 0 }}>
                    Latest insights and best practices
                  </p>
                </a>

              </div>
            </div>
            */}

            <a 
              href="/careers"
              style={{
                color: "#d1d5db",
                fontWeight: 500,
                fontSize: "1rem",
                borderRadius: "8px",
                transition: "color 0.2s",
                textDecoration: "none",
                padding: "0.5rem",
              }}
              onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
            >
              Careers
            </a>

            <a
              href="/contactus"
              className="flex items-center gap-2 font-medium transition-all"
              style={{
                padding: "0.75rem 1.5rem",
                background: "rgba(26, 26, 46, 0.6)",
                color: "#ffffff",
                borderRadius: "12px",
                fontSize: "1.05rem",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.2)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(16px) saturate(180%)",
                outline: "none",
                transform: "none",
                textDecoration: "none",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseOver={e => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 168, 107, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 168, 107, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
              }}
            >
              <Lock className="w-4 h-4" />
              Get Started
            </a>
            
            {/* Mobile Click-to-Call */}
            <div className="md:hidden">
              <ClickToCall 
                variant="icon"
                size="sm"
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
              />
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={toggleMenu}
              className="p-2"
              style={{
                color: "#d1d5db",
                background: "none",
                borderRadius: "8px",
                transition: "color 0.2s, background 0.2s",
              }}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <div
        className={cn(
          "md:hidden transition-all duration-300 overflow-hidden w-full",
          isMenuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        )}
        style={{
          background: "rgba(26, 26, 46, 0.4)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px) saturate(180%)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
        }}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 shadow-sm">
          <Link
            to="/"
            className="block px-3 py-2"
            style={{
              borderRadius: "8px",
              color: "#d1d5db",
              fontWeight: 500,
              fontSize: "1rem",
              background: "none",
              transition: "color 0.2s, background 0.2s",
            }}
            onClick={() => {
              setIsMenuOpen(false);
              window.scrollTo(0, 0);
            }}
            onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
            onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
          >
            Home
          </Link>

          <div className="block">
            <button
              onClick={e => {
                e.preventDefault();
                const submenu = e.currentTarget.nextElementSibling;
                if (submenu) {
                  submenu.classList.toggle('hidden');
                }
              }}
              className="flex w-full justify-between items-center px-3 py-2"
              style={{
                borderRadius: "8px",
                color: "#d1d5db",
                fontWeight: 500,
                fontSize: "1rem",
                background: "none",
                transition: "color 0.2s, background 0.2s",
              }}
              aria-expanded="false"
              aria-controls="mobile-services-menu"
              onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
            >
              <span>Services</span>
              <ChevronDown className="h-4 w-4" />
            </button>

            <div className="hidden ml-4 mt-1 space-y-1" id="mobile-services-menu">
              <Link
                to="/services/application-security"
                className="flex items-center w-full text-left px-3 py-2 gap-2"
                style={{
                  borderRadius: "8px",
                  color: "#d1d5db",
                  fontSize: "0.95rem",
                  background: "none",
                  transition: "color 0.2s, background 0.2s",
                  textDecoration: "none"
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                <Shield className="w-4 h-4" style={{ color: "#00a86b" }} />
                Application Security
              </Link>
              <Link
                to="/services/security-advisory"
                className="flex items-center w-full text-left px-3 py-2 gap-2"
                style={{
                  borderRadius: "8px",
                  color: "#d1d5db",
                  fontSize: "0.95rem",
                  background: "none",
                  transition: "color 0.2s, background 0.2s",
                  textDecoration: "none"
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                <ShieldCheck className="w-4 h-4" style={{ color: "#a78bfa" }} />
                Security Advisory Services
              </Link>
              <Link
                to="/services/cloud-security"
                className="flex items-center w-full text-left px-3 py-2 gap-2"
                style={{
                  borderRadius: "8px",
                  color: "#d1d5db",
                  fontSize: "0.95rem",
                  background: "none",
                  transition: "color 0.2s, background 0.2s",
                  textDecoration: "none"
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                <Cloud className="w-4 h-4" style={{ color: "#3b82f6" }} />
                Cloud Security
              </Link>
              <Link
                to="/services/offensive-security"
                className="flex items-center w-full text-left px-3 py-2 gap-2"
                style={{
                  borderRadius: "8px",
                  color: "#d1d5db",
                  fontSize: "0.95rem",
                  background: "none",
                  transition: "color 0.2s, background 0.2s",
                  textDecoration: "none"
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                <Target className="w-4 h-4" style={{ color: "#f87171" }} />
                Offensive Security
              </Link>
              <Link
                to="/services/devsecops"
                className="flex items-center w-full text-left px-3 py-2 gap-2"
                style={{
                  borderRadius: "8px",
                  color: "#d1d5db",
                  fontSize: "0.95rem",
                  background: "none",
                  transition: "color 0.2s, background 0.2s",
                  textDecoration: "none"
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                <Terminal className="w-4 h-4" style={{ color: "#4ade80" }} />
                DevSecOps
              </Link>
              <Link
                to="/services"
                className="flex items-center justify-center gap-2 px-3 py-2 mt-3"
                style={{
                  borderRadius: "12px",
                  background: "rgba(26, 26, 46, 0.6)",
                  color: "#ffffff",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  transition: "all 0.2s",
                  border: "1px solid rgba(0, 168, 107, 0.3)",
                  boxShadow: "0 4px 16px rgba(0, 168, 107, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                  backdropFilter: "blur(16px) saturate(180%)"
                }}
                onClick={() => setIsMenuOpen(false)}
                onMouseOver={e => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.background = "rgba(0, 168, 107, 0.2)";
                  e.currentTarget.style.borderColor = "rgba(0, 168, 107, 0.5)";
                }}
                onMouseOut={e => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.background = "rgba(26, 26, 46, 0.6)";
                  e.currentTarget.style.borderColor = "rgba(0, 168, 107, 0.3)";
                }}
              >
                View All Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <Link
            to="/about"
            className="block px-3 py-2"
            style={{
              borderRadius: "8px",
              color: "#d1d5db",
              fontWeight: 500,
              fontSize: "1rem",
              background: "none",
              transition: "color 0.2s, background 0.2s",
            }}
            onClick={() => {
              setIsMenuOpen(false);
              window.scrollTo(0, 0);
            }}
            onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
            onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
          >
            About
          </Link>

{/*
          <div className="block">
            <button
              onClick={e => {
                e.preventDefault();
                const submenu = e.currentTarget.nextElementSibling;
                if (submenu) {
                  submenu.classList.toggle('hidden');
                }
              }}
              className="flex w-full justify-between items-center px-3 py-2"
              style={{
                borderRadius: "8px",
                color: "#d1d5db",
                fontWeight: 500,
                fontSize: "1rem",
                background: "none",
                transition: "color 0.2s, background 0.2s",
              }}
              aria-expanded="false"
              aria-controls="mobile-resources-menu"
              onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
              onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
            >
              <span>Resources</span>
              <ChevronDown className="h-4 w-4" />
            </button>

            <div className="hidden ml-4 mt-1 space-y-1" id="mobile-resources-menu">
              <Link
                to="/blog"
                className="block px-3 py-2"
                style={{
                  borderRadius: "8px",
                  color: "#d1d5db",
                  fontSize: "0.95rem",
                  background: "none",
                  transition: "color 0.2s, background 0.2s",
                }}
                onClick={() => {
                  setIsMenuOpen(false);
                  window.scrollTo(0, 0);
                }}
                onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
                onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
              >
                Security Blog
              </Link>
            </div>
          </div>
          */}

          <Link
            to="/careers"
            className="block px-3 py-2"
            style={{
              borderRadius: "8px",
              color: "#d1d5db",
              fontWeight: 500,
              fontSize: "1rem",
              background: "none",
              transition: "color 0.2s, background 0.2s",
            }}
            onClick={() => {
              setIsMenuOpen(false);
              window.scrollTo(0, 0);
            }}
            onMouseOver={e => (e.currentTarget.style.color = "#ffffff")}
            onMouseOut={e => (e.currentTarget.style.color = "#d1d5db")}
          >
            Careers
          </Link>

          <div className="pt-4 px-3">
            <Link
              to="/contactus"
              className="flex items-center justify-center gap-2 w-full font-medium transition-all"
              style={{
                padding: "1rem 2rem",
                background: "linear-gradient(135deg, #00a86b 0%, #06b6d4 100%)",
                color: "#ffffff",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: 600,
                border: "1px solid rgba(0, 168, 107, 0.3)",
                boxShadow: "0 4px 16px rgba(0, 168, 107, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(16px) saturate(180%)",
                outline: "none",
                transform: "none",
                textDecoration: "none",
              }}
              aria-label="Get Started"
              tabIndex={0}
              onMouseOver={e => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 8px 32px rgba(0, 168, 107, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
              }}
              onMouseOut={e => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 168, 107, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
              }}
              onFocus={e => {
                e.currentTarget.style.boxShadow = "0 0 0 3px rgba(0, 168, 107, 0.3), 0 8px 32px rgba(0, 168, 107, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onBlur={e => {
                e.currentTarget.style.boxShadow = "0 4px 16px rgba(0, 168, 107, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <Lock className="w-4 h-4" />
              Get Started
            </Link>
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;